import React, { useCallback, useEffect, useState } from "react";
import "./SimpleBarGraph.component.scss";

interface BarData {
  label: string; // label for subsection of bar
  color: string; // color of subsection of bar
  value: number; // value for subsection of bar
}

interface Dataset {
  label: string; // label for full bar
  data: BarData[]; // first in list determines bottom of section of vertical bar and left section of horizontal bar
}

interface SimpleBarGraphProps {
  id: string;
  isVertical: boolean;
  datasets: Dataset[]; // first in list determines leftmost bar if vertical graph and topmost of horizontal graph
  gap: number;
  barSize: number;
  maxSize?: number;
  graphSize?: number;
  barBorderRadius?: string;
  hasSubsections?: boolean;
  barTotal?: number;
  onGraphClick?: () => void;
}

const SimpleBarGraph: React.FC<SimpleBarGraphProps> = ({
  id,
  isVertical = true,
  gap,
  datasets,
  barSize,
  maxSize,
  graphSize,
  barBorderRadius = "0.5rem",
  hasSubsections,
  barTotal,
  onGraphClick
}) => {
  const [mapData, setMapData] = useState<Dataset[]>([]);
  if (datasets.length === 0) {
    return null;
  }
  const graphDimValue = graphSize !== undefined ? `${graphSize}rem` : '100%';

  useEffect(() => {
    if (datasets.length > 0) {
      const newMapData = structuredClone(datasets) as Dataset[];
      const lastMapData = newMapData[newMapData.length - 1];

      // combine bars that have value 0 for the last bar in the list
      // this is done in order to combine labels in this format 'Tier 1, 2'
      newMapData[newMapData.length - 1] = {
        ...lastMapData,
        data: lastMapData.data.reduce((acc, cur) => {
          if (cur.value === 0 && acc.length > 0) {
            const prevSection = acc[acc.length - 1];
            if (prevSection.value === 0) {
              prevSection.label = `${cur.label},${prevSection.label.replace(cur.label.split(" ")[0], "")}`;
            } else {
              acc.push(cur);
            }
          } else {
            acc.push(cur);
          }

          return acc;
        }, [] as BarData[]),
      };

      setMapData(newMapData);
    }
  }, [datasets]);

  const renderLeftColumnLabels = useCallback(
    () => (
      <div
        className={`d-flex flex-column flex-nowrap align-items-end justify-content-between`}
      >
        {mapData &&
          mapData.map((bar, index) => {
            const tick = bar.label;
            return (
              <div key={`${tick}-${index}`} className="sbg-tick sgb-tick-horizontal d-flex flex-column justify-content-center" style={{
                height: `${barSize}rem`,
              }}>
                {tick}
              </div>
            );
          })}
      </div>
    ),
    [mapData, barSize]
  );

  const renderBarSubsectionLabels = useCallback((firstBar: boolean, percentFloatValue: number, secLabel: string)=> {
    if (hasSubsections && firstBar) {
      return (
        <span
          className={`sbg-label sbg-label-first-column ${percentFloatValue <= 0.1 && percentFloatValue !== 0 ? "sbg-label-extended" : ""}`}
        >
          {secLabel}
          <div className="sbg-label-line"></div>
        </span>
      );
    } else if (!hasSubsections) {
      return (
        <span
          className={`sbg-single-bar-label sbg-label sbg-label-${isVertical ? "vertical" : "horizontal"}`}
        >
          {secLabel}
          <div className="sbg-label-line"></div>
        </span>
      );
    }
    return null;
  }, [mapData, isVertical, hasSubsections]);

  const calculateBarBorders = useCallback((subIndex: number, bar: Dataset): string => {
    let topRadius = `0 0`;
    let botRadius = `0 0`;
    if (
      subIndex === 0 ||
      (subIndex === 1 && bar.data[0].value === 0)
    ) {
      botRadius = `${barBorderRadius} ${barBorderRadius}`;
    }
    if (
      subIndex === bar.data.length - 1 ||
      (subIndex === bar.data.length - 2 &&
        bar.data[bar.data.length - 1].value === 0)
    ) {
      topRadius = `${barBorderRadius} ${barBorderRadius}`;
    }
    const borderRadius = isVertical
      ? `${topRadius} ${botRadius}`
      : `${botRadius.split(" ")[0]} ${topRadius.split(" ")[0]} ${topRadius.split(" ")[1]} ${botRadius.split(" ")[1]}`;

    return borderRadius;
  }, [mapData, isVertical]);

  let barClasses = "";
  if (hasSubsections) {
    barClasses = isVertical
      ? "sbg-bar-graph-vertical"
      : "sbg-bar-graph-horizontal";
  } else {
    barClasses = isVertical
      ? "sbg-simple-bar-graph-vertical"
      : "sbg-simple-bar-graph-horizontal";
  }

  return (
    <div
      className={`sbg-bar-graph ${isVertical ? "flex-row justify-content-center" : "flex-column"} ${barClasses} ${graphDimValue === '100%' ? 'flex-grow-1' : ''}`}
      style={{height: isVertical && graphDimValue === '100%' ? '100%' : 'unset'}}
      data-testid={`${id}-bar-graph`}
    >
      <div
        className={`d-flex flex-nowrap position-relative ${isVertical ? "flex-column-reverse" : ""}`}
        style={{height: isVertical && graphDimValue === '100%' ? '100%' : 'unset', cursor: onGraphClick ? 'pointer' : 'default'}}
        onClick={onGraphClick}

      >
        {!isVertical && renderLeftColumnLabels()}
        <div
          className={`d-flex ${isVertical ? "flex-row" : "flex-column"} flex-nowrap ${maxSize === undefined ? 'align-items-end' : ''} gap-${gap}`}
          style={{
            width: `${!isVertical ? graphDimValue : "unset"}`,
            height: `${isVertical ? graphDimValue : "unset"}`,
          }}
        >
          {mapData &&
            mapData.map((bar, index) => {
              const tick = bar.label;
              const total = bar.data.reduce((acc, b) => acc + b.value, 0);
              const isFirstBar = index === 0;

              return (
                <div
                  key={`${tick}-${index}`}
                  data-testid={`${id}-bar-${index}`}
                  className={`d-flex ${isVertical ? "flex-column-reverse sgb-bar-container-vertical" : "flex-row sgb-bar-container-horizontal"} flex-nowrap align-items-center`}
                  style={{
                    width: `${!isVertical ? graphDimValue : "unset"}`,
                    height: `${isVertical ? graphDimValue : "unset"}`,
                    maxWidth: `${!isVertical ? maxSize+'rem' : "unset"}`,
                    maxHeight: `${isVertical ? maxSize+'rem' : "unset"}`,
                  }}
                >
                  {isVertical && (
                    <div key={`${tick}-${index}`} className="sbg-tick">
                      {tick}
                    </div>
                  )}
                  {bar.data.map((section, subIndex) => {
                    const {
                      color: secColor,
                      value: secVal,
                      label: secLabel,
                    } = section;

                    const percentFloatValue =
                      secVal / (barTotal !== undefined ? barTotal : total);

                    let legendDisplay = renderBarSubsectionLabels(isFirstBar, percentFloatValue, secLabel);

                    // don't render a bar if value 0 just label
                    if (secVal === 0) {
                      return (
                        <div
                          key={`${tick}-${secLabel.replaceAll(' ', '_')}-${subIndex}`}
                          className={`position-relative ${isVertical ? "w-100" : "h-100"}`}
                        >
                          {legendDisplay}
                        </div>
                      );
                    }

                    // handle border radius
                    const borderRadius = calculateBarBorders(subIndex, bar);

                    return (
                      <div
                        key={`${tick}-${secLabel.replaceAll(' ', '_')}-${subIndex}`}
                        className={`bar bar-${isVertical ? 'vertical' : 'horizontal'} position-relative`}
                        style={{
                          width: `${isVertical ? barSize + "rem" : "unset"}`,
                          height: `${!isVertical ? barSize + "rem" : "unset"}`,
                          flex: `${percentFloatValue}`,
                          background: secColor,
                          borderRadius,
                        }}
                      >
                        {legendDisplay}
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default SimpleBarGraph;
